import { useEffect } from "react";

import { Lightbulb } from "lucide-react";
import {
  Navigate,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router";

import { isXsToLgQuery, Media } from "@/components/layout/Media";
import { Authenticate } from "@/components/pages_logged_in/Authenticate";
import { ClientWorkspaceProvider } from "@/components/pages_logged_in/workspace/clients/ClientWorkspaceContext";
import { NotFound } from "@/components/pages_public/404/NotFound";
import { BackButton } from "@/components/pieces/onboarding/BackButton";
import { Header } from "@/components/pieces/onboarding/Header";
import { ProductPrice } from "@/components/pieces/products/ProductPrice";
import { Button } from "@/components/ui/button";
import { Callout } from "@/components/ui/callout";
import { Card, CardContent } from "@/components/ui/card";
import { ProductCategories, useGetIntroMeetingSuspenseQuery } from "@/graphql";

import { CoachDescriptionCard } from "./CoachDescriptionCard";
import { BookClientAction } from "./clients/BookClientAction";
import { ClientSelector } from "./clients/ClientSelector";
import { ProductSelector } from "./products/ProductSelector";
import { useProducts } from "./products/use-products";
import { ProgramCard } from "./programs/ProgramCard";
import { ProgramCardSkeleton } from "./programs/ProgramCardSkeleton";
import { SessionCard } from "./programs/SessionCard";
import { usePrograms } from "./programs/use-programs";
import { useSession } from "./programs/use-session";
import { StackedTimeslotLoadingState } from "./timeslots/StackedTimeslotLoadingState";
import { TimeslotCard } from "./timeslots/TimeslotCard";
import { useBookCoach } from "./use-book-coach";
import { useScheduleState } from "./use-schedule-state";

export function AuthSchedule({ coachId }: { coachId: string }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("programComponentId");

  // Determine if this is a match booking flow
  const isMatch = searchParams.get("match") === "true";

  // redirect if intro meeting is upcoming
  const { data } = useGetIntroMeetingSuspenseQuery({
    variables: {
      coachId,
    },
    skip: !!sessionId,
  });
  if (data?.introMeeting) {
    const url = "/sessions/" + data.introMeeting.id + "?showIntroMessage=true";
    return <Navigate to={url} replace />;
  }

  // scroll to top
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // use schedule state -- see documentation in use-schedule-state.ts
  const { state, dispatch } = useScheduleState();

  // use session
  const { loading: sessionLoading } = useSession({
    coachId,
    errorMessage: state.errorMessage,
    selectedSession: state.selectedSession,
    onSelectSession: (session) => {
      dispatch({ type: "SELECT_SESSION", payload: session });
    },
    onSelectProduct: (product, coachProduct) => {
      dispatch({ type: "SELECT_PRODUCT", payload: { product, coachProduct } });
    },
    onSessionError: (errorMessage) => {
      dispatch({ type: "SET_ERROR_MESSAGE", payload: errorMessage });
    },
  });

  // use programs -- see documentation in use-programs.ts
  const { programs } = usePrograms({
    coachId,
    workspaceId: state.selectedWorkspaceId ?? "",
    clientId: state.selectedClientId ?? "",
    showPrograms: state.showPrograms,
    programsInitialized: state.programsInitialized,
    onSelectSession: (session) => {
      dispatch({ type: "SELECT_SESSION", payload: session });
    },
    onSelectProduct: (product, coachProduct) => {
      dispatch({ type: "SELECT_PRODUCT", payload: { product, coachProduct } });
    },
    onSetShowProducts: (showProducts) => {
      dispatch({ type: "SHOW_PRODUCTS", payload: showProducts });
    },
    onSelectProgramId: (programId) => {
      dispatch({ type: "SELECT_PROGRAM", payload: programId });
    },
    onSetWarningMessage: (message) => {
      dispatch({ type: "SET_WARNING_MESSAGE", payload: message });
    },
    onSetShowPrograms: (showPrograms) => {
      dispatch({ type: "SHOW_PROGRAMS", payload: showPrograms });
    },
    onSetProgramsInitialized: (initialized) => {
      dispatch({ type: "SET_PROGRAMS_INITIALIZED", payload: initialized });
    },
  });

  // use products (only runs after programs are initialized)
  const {
    specialties,
    coachProducts,
    loading: productsLoading,
  } = useProducts({
    coachId,
    selectedWorkspaceId: state.selectedWorkspaceId,
    selectedClientId: state.selectedClientId,
    selectedProduct: state.selectedProduct,
    showPrograms: state.showPrograms,
    onSelectProduct: (product, coachProduct) => {
      dispatch({
        type: "SELECT_PRODUCT",
        payload: { product, coachProduct },
      });
    },
    onSelectSpecialtyId: (specialtyId) => {
      dispatch({ type: "SELECT_SPECIALTY", payload: specialtyId });
    },
    onSetWarningMessage: (message) => {
      dispatch({ type: "SET_WARNING_MESSAGE", payload: message });
    },
    // Skip products query until programs are initialized
    skip: !state.programsInitialized || !state.showProducts,
  });

  // use book coach hook
  const { handleBooking, loading: isSubmitting } = useBookCoach();

  const handleSubmit = () => {
    handleBooking({
      coachId,
      state,
      onError: (message) => {
        dispatch({ type: "SET_ERROR_MESSAGE", payload: message });
      },
    });
  };

  const isReview = state.selectedProduct?.category === ProductCategories.Review;
  const isTimeMissing = !isReview && state.selectedTimeslots.length === 0;
  const disableSubmit = isSubmitting || isTimeMissing || !state.selectedProduct;

  const buttonText = state.selectedCoachProduct?.requiresBookingApproval
    ? "Request Booking"
    : "Book Now";

  const backButton =
    state.backUrl && isMatch ? (
      <BackButton
        hoverMessage="Select a different coach"
        onClick={() => navigate(state.backUrl!)}
        mediaQuery={isXsToLgQuery}
        mt={[0, 12]}
      />
    ) : undefined;

  return (
    <div>
      <Media at="xs">
        <Header linkToDash={true} backButton={backButton} />
      </Media>
      <Media greaterThan="xs">
        <Header linkToDash={true} />
      </Media>
      <div
        className={`relative px-3 lg:px-0 ${isMatch ? "my-[70px] md:my-[110px] lg:my-[130px]" : "my-16 lg:my-24"}`}
      >
        <Media greaterThan="xs">{backButton}</Media>
        <div className="flex flex-col items-center justify-center">
          <div className="flex flex-col gap-4 mt-4 font-semibold w-full max-w-2xl">
            <CoachDescriptionCard coachId={coachId} />

            {state.warningMessage && (
              <div className="flex items-start gap-3 rounded-lg bg-amber-50 p-4">
                <Lightbulb className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-amber-700 font-normal">
                  <strong>Heads up</strong> – {state.warningMessage}
                </div>
              </div>
            )}

            {state.selectedClientId && (
              <ClientWorkspaceProvider clientId={state.selectedClientId}>
                <ClientSelector
                  coachId={coachId}
                  selectedWorkspaceId={state.selectedWorkspaceId}
                  selectedClientId={state.selectedClientId}
                  onSelectClient={(clientId) =>
                    dispatch({ type: "SELECT_CLIENT", payload: clientId })
                  }
                  onSelectWorkspace={(workspaceId) => {
                    dispatch({
                      type: "SELECT_WORKSPACE",
                      payload: workspaceId,
                    });
                  }}
                />
              </ClientWorkspaceProvider>
            )}

            {state.selectedSession &&
              !state.showPrograms &&
              !state.showProducts &&
              state.selectedProduct && (
                <SessionCard
                  session={state.selectedSession}
                  product={state.selectedProduct}
                />
              )}

            {state.showPrograms && !state.warningMessage && (
              <>
                {!state.programsInitialized ? (
                  <ProgramCardSkeleton />
                ) : (
                  <ProgramCard
                    selectedProduct={state.selectedProduct}
                    programs={programs}
                    selectedProgramId={state.selectedProgramId}
                    onSelectProgramId={(programId) =>
                      dispatch({ type: "SELECT_PROGRAM", payload: programId })
                    }
                    selectedSession={state.selectedSession}
                    onSelectSession={(session) =>
                      dispatch({ type: "SELECT_SESSION", payload: session })
                    }
                    onSelectProduct={(product, coachProduct) => {
                      dispatch({
                        type: "SELECT_PRODUCT",
                        payload: { product, coachProduct },
                      });
                    }}
                    tab={state.tab}
                    onTabChange={(tab) => {
                      dispatch({ type: "CHANGE_TAB", payload: tab });
                    }}
                  />
                )}
              </>
            )}

            {state.showProducts && (
              <ProductSelector
                specialties={specialties}
                coachProducts={coachProducts}
                selectedProduct={state.selectedProduct}
                selectedSpecialtyId={state.selectedSpecialtyId}
                onSelectProduct={(product, coachProduct) =>
                  dispatch({
                    type: "SELECT_PRODUCT",
                    payload: { product, coachProduct },
                  })
                }
                onSelectSpecialtyId={(specialtyId) =>
                  dispatch({ type: "SELECT_SPECIALTY", payload: specialtyId })
                }
                loading={productsLoading}
              />
            )}

            <Card>
              <CardContent>
                {sessionLoading && <StackedTimeslotLoadingState />}

                {state.selectedProduct &&
                  state.selectedCoachProduct &&
                  !isReview && (
                    <>
                      <TimeslotCard
                        coachId={coachId}
                        productId={state.selectedProduct.id}
                        requiresBookingApproval={
                          state.selectedCoachProduct.requiresBookingApproval ??
                          false
                        }
                        variant="stacked"
                        onSelectTimeslots={(timeslots) =>
                          dispatch({
                            type: "SELECT_TIMESLOTS",
                            payload: timeslots,
                          })
                        }
                        selectedTimeslots={state.selectedTimeslots}
                        showAllTimes={state.showAllTimes}
                        setShowAllTimes={(show) =>
                          dispatch({ type: "SHOW_ALL_TIMES", payload: show })
                        }
                        isViewOnly={false}
                        start={state.selectedSession?.targetStartDate}
                        end={state.selectedSession?.targetEndDate}
                        preferredTimeslots={state.preferredTimeslots}
                        onPreferredChange={(preferredTimeslots) =>
                          dispatch({
                            type: "SET_PREFERRED_TIMESLOTS",
                            payload: preferredTimeslots,
                          })
                        }
                      />
                    </>
                  )}

                {state.selectedCoachProduct && (
                  <>
                    <div className="my-4 border-t border-gray-300" />
                    <ProductPrice
                      creditPrice={state.selectedCoachProduct.coachPrice}
                    />
                  </>
                )}

                <>
                  {state.selectedClientId &&
                  state.selectedProduct &&
                  state.selectedCoachProduct ? (
                    <BookClientAction
                      coachId={coachId}
                      clientId={state.selectedClientId}
                      product={state.selectedProduct}
                      coachProduct={state.selectedCoachProduct}
                      selectedWorkspaceId={state.selectedWorkspaceId}
                      sessionId={state.selectedSession?.id}
                      timeslots={state.selectedTimeslots}
                      showAllTimes={state.showAllTimes}
                      errorMessage={state.errorMessage}
                      onSetErrorMessage={(error) =>
                        dispatch({ type: "SET_ERROR_MESSAGE", payload: error })
                      }
                    />
                  ) : (
                    <div className="flex flex-col justify-center mt-4">
                      {state.errorMessage && (
                        <Callout
                          variant="error"
                          title="There was a problem with your request."
                          className="mb-4"
                        >
                          {state.errorMessage}
                        </Callout>
                      )}
                      <Button
                        onClick={handleSubmit}
                        size="lg"
                        variant="solid"
                        disabled={disableSubmit}
                        className="w-full"
                      >
                        {isSubmitting && (
                          <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                        )}
                        {isSubmitting ? "Booking..." : buttonText}
                      </Button>
                    </div>
                  )}
                </>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}

export function SchedulePage() {
  const { coachId } = useParams();

  if (!coachId) return <NotFound />;

  return (
    <Authenticate layout="compact" showMessageBar={false} maxWidth={"100%"}>
      <AuthSchedule coachId={coachId} />
    </Authenticate>
  );
}
